import { useRef, useCallback } from 'react'

interface LayoutToggleProps {
  value: 'grid' | 'masonry'
  onChange: (value: 'grid' | 'masonry') => void
}

const activeClass = 'bg-blue-600 text-white border-blue-600 shadow-sm'
const inactiveClass = 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'

export const LayoutToggle: React.FC<LayoutToggleProps> = ({ value, onChange }) => {
  const gridRef = useRef<HTMLButtonElement>(null)
  const masonryRef = useRef<HTMLButtonElement>(null)

  const handleChange = useCallback((targetValue: 'grid' | 'masonry') => {
    if (value === targetValue) return
    onChange(targetValue)
  }, [value, onChange])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      const nextValue = value === 'grid' ? 'masonry' : 'grid'
      handleChange(nextValue)
      if (nextValue === 'grid') {
        gridRef.current?.focus()
      } else {
        masonryRef.current?.focus()
      }
    }
  }, [value, handleChange])

  return (
    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="radiogroup" aria-label="Layout mode">
      <button
        ref={gridRef}
        role="radio"
        aria-checked={value === 'grid'}
        aria-label="Grid layout"
        tabIndex={value === 'grid' ? 0 : -1}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-r border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${value === 'grid' ? activeClass : inactiveClass}`}
        onClick={() => handleChange('grid')}
        onKeyDown={handleKeyDown}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
          <rect x="0" y="0" width="6" height="6" rx="0.5" />
          <rect x="8" y="0" width="6" height="6" rx="0.5" />
          <rect x="0" y="8" width="6" height="6" rx="0.5" />
          <rect x="8" y="8" width="6" height="6" rx="0.5" />
        </svg>
        <span>Grid</span>
      </button>
      <button
        ref={masonryRef}
        role="radio"
        aria-checked={value === 'masonry'}
        aria-label="Masonry layout"
        tabIndex={value === 'masonry' ? 0 : -1}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${value === 'masonry' ? activeClass : inactiveClass}`}
        onClick={() => handleChange('masonry')}
        onKeyDown={handleKeyDown}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
          <rect x="0" y="0" width="6" height="8" rx="0.5" />
          <rect x="8" y="0" width="6" height="5" rx="0.5" />
          <rect x="0" y="9" width="6" height="5" rx="0.5" />
          <rect x="8" y="6" width="6" height="8" rx="0.5" />
        </svg>
        <span>Masonry</span>
      </button>
    </div>
  )
}
